import { UserName } from "./UserName.tsx";
import { useCallback, useEffect, useState } from "react";
import type { DuelHistoryEntry, PublicUser } from "@mk/shared";
import { duelRulesSummary } from "../features/duel/rules.ts";
import { duelResultText } from "../features/duel/resultText.ts";
import { api } from "../lib/api.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { useSession } from "../lib/session.ts";
import { ResultBoard } from "./ResultBoard.tsx";
import { Sheet } from "./Sheet.tsx";
import { messages } from "../i18n/index.ts";
import { formatDate } from "../lib/edition.ts";

type Outcome = "win" | "loss" | "draw";

const OUTCOME_LABEL: Record<Outcome, string> = messages.friendHistory.badge;
const OUTCOME_TITLE: Record<Outcome, string> = {
  win: messages.friendHistory.win,
  loss: messages.friendHistory.loss,
  draw: messages.friendHistory.draw,
};
const OUTCOME_ACCENT: Record<Outcome, "mint" | "flame" | "amber"> = {
  win: "mint",
  loss: "flame",
  draw: "amber",
};
const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
};

interface FriendHistorySheetProps {
  friendshipId: string | null;
  friend: PublicUser | null;
  onClose: () => void;
  registerBackHandler?: RegisterBackHandler;
}

function outcomeOf(entry: DuelHistoryEntry, meId: string | undefined): Outcome {
  if (entry.winnerId === null) return "draw";
  return entry.winnerId === meId ? "win" : "loss";
}

/** Kabul edilmiş iki arkadaş arasındaki, kategori ayrımı yapmayan son on maç. */
export function FriendHistorySheet({
  friendshipId,
  friend,
  onClose,
  registerBackHandler,
}: FriendHistorySheetProps) {
  const meId = useSession((state) => state.user?.id);
  const [entries, setEntries] = useState<DuelHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<DuelHistoryEntry | null>(null);
  const open = friendshipId !== null && friend !== null;

  useEffect(() => {
    if (!friendshipId) {
      setEntries(null);
      setError(null);
      setSelected(null);
      return;
    }
    let cancelled = false;
    setEntries(null);
    setError(null);
    api<{ entries: DuelHistoryEntry[] }>(`/api/friends/${friendshipId}/history`)
      .then((data) => {
        if (!cancelled) setEntries(data.entries);
      })
      .catch(() => {
        if (!cancelled) setError(messages.friendHistory.loadFailed);
      });
    return () => {
      cancelled = true;
    };
  }, [friendshipId]);

  const handleBack = useCallback(() => {
    if (selected) setSelected(null);
    else onClose();
  }, [onClose, selected]);

  useEffect(() => {
    if (!open || !registerBackHandler) return;
    return registerBackHandler(() => {
      handleBack();
      return "block";
    });
  }, [handleBack, open, registerBackHandler]);

  const selectedOutcome = selected ? outcomeOf(selected, meId) : null;

  return (
    <Sheet
      open={open}
      title={
        selected && selectedOutcome
          ? OUTCOME_TITLE[selectedOutcome]
          : messages.friendHistory.lastTen(friend?.username ?? messages.common.friend)
      }
      accent={selectedOutcome ? OUTCOME_ACCENT[selectedOutcome] : "mint"}
      placement="center"
      onClose={onClose}
    >
      {selected ? (
        <>
          <p className="text-sm text-muted">{duelResultText(selected, meId)}</p>
          <p className="mt-1 text-xs text-dim">
            {formatDate(selected.finishedAt, DATE_OPTIONS)}
          </p>
          <p className="mt-2 text-xs leading-relaxed text-muted">
            {selected.category === "friendly" ? messages.common.friendlyMatch : messages.common.standardDuel} ·{" "}
            {duelRulesSummary(selected.rules)}
          </p>

          <div className="answer-row mt-4">
            {[...selected.answer].map((letter, index) => (
              <span key={index} className="tile" data-filled="true" data-state="correct">
                {letter}
              </span>
            ))}
          </div>

          <div className="duel-result-grid mt-6">
            <div className="duel-result-card plate-l">
              <span className="duel-compare__name">
                {messages.common.you} <span className="text-mint">· {selected.you.attempts}</span>
              </span>
              <ResultBoard rows={selected.you.guesses} />
            </div>
            <div className="duel-result-card plate-r">
              <span className="duel-compare__name">
                {<UserName user={selected.opponent.user} />}{" "}
                <span className="text-flame">· {selected.opponent.attempts}</span>
              </span>
              <ResultBoard rows={selected.opponent.guesses} />
            </div>
          </div>

          <button type="button" className="btn-secondary mt-6 w-full" onClick={() => setSelected(null)}>
            {messages.common.returnToMatches}
          </button>
        </>
      ) : (
        <div className="flex max-h-[65vh] flex-col gap-2 overflow-y-auto">
          {entries === null && !error && <p className="text-sm text-muted">{messages.common.loading}</p>}
          {error && <p className="text-sm text-muted">{error}</p>}
          {entries?.length === 0 && (
            <p className="text-sm leading-relaxed text-muted">
              {messages.friendHistory.empty}
            </p>
          )}
          {entries?.map((entry) => {
            const outcome = outcomeOf(entry, meId);
            return (
              <button
                key={entry.matchId}
                type="button"
                className="history-row"
                data-outcome={outcome}
                onClick={() => setSelected(entry)}
              >
                <span className="history-row__badge">{OUTCOME_LABEL[outcome]}</span>
                <span className="history-row__main">
                  <span className="history-row__name">
                    {entry.category === "friendly" ? messages.common.friendlyMatch : messages.common.standardDuel}
                  </span>
                  <span className="history-row__date">
                    {formatDate(entry.finishedAt, DATE_OPTIONS)}
                  </span>
                </span>
                <span className="history-row__score">
                  {entry.you.solved ? entry.you.attempts : "—"}
                  <span aria-hidden="true"> : </span>
                  {entry.opponent.solved ? entry.opponent.attempts : "—"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}
